import { useEffect, useRef, type DragEvent, type PointerEvent } from "react";

export function AccountFidgetSpinner() {
  const containerRef = useRef<HTMLDivElement>(null);
  const rotorRef = useRef<HTMLDivElement>(null);
  const rotationRef = useRef(0);
  const velocityRef = useRef(0);
  const draggingRef = useRef(false);
  const lastAngleRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);

  const stopSpinLoop = () => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  };

  const spinStep = () => {
    if (!draggingRef.current) {
      velocityRef.current *= 0.988;
      if (Math.abs(velocityRef.current) < 0.025) {
        velocityRef.current = 0;
      }
    }
    rotationRef.current += velocityRef.current;
    if (rotorRef.current) {
      rotorRef.current.style.transform = `rotate(${rotationRef.current}deg)`;
    }
    if (draggingRef.current || Math.abs(velocityRef.current) > 0.001) {
      rafRef.current = requestAnimationFrame(spinStep);
    } else {
      rafRef.current = null;
    }
  };

  const ensureSpinLoop = () => {
    if (rafRef.current == null) {
      rafRef.current = requestAnimationFrame(spinStep);
    }
  };

  useEffect(() => {
    return () => stopSpinLoop();
  }, []);

  const angleFromEvent = (clientX: number, clientY: number) => {
    const el = containerRef.current;
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    return (Math.atan2(clientY - cy, clientX - cx) * 180) / Math.PI;
  };

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    draggingRef.current = true;
    lastAngleRef.current = angleFromEvent(e.clientX, e.clientY);
    velocityRef.current = 0;
    ensureSpinLoop();
  };

  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current || lastAngleRef.current === null) return;
    e.preventDefault();
    const angle = angleFromEvent(e.clientX, e.clientY);
    let delta = angle - lastAngleRef.current;
    if (delta > 180) delta -= 360;
    if (delta < -180) delta += 360;
    velocityRef.current += delta * 0.18;
    lastAngleRef.current = angle;
    ensureSpinLoop();
  };

  const endDrag = (e: PointerEvent<HTMLDivElement>) => {
    draggingRef.current = false;
    lastAngleRef.current = null;
    ensureSpinLoop();
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  };

  return (
    <div
      ref={containerRef}
      className="pointer-events-auto ml-auto size-7 shrink-0 cursor-pointer touch-none opacity-0 select-none group-hover:opacity-75 hover:!opacity-100 dark:group-hover:opacity-70"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onDragStart={(e: DragEvent<HTMLDivElement>) => e.preventDefault()}
      role="presentation"
      aria-hidden
    >
      <div
        ref={rotorRef}
        className="flex size-full items-center justify-center text-blue-500 will-change-transform dark:text-blue-400"
      >
        <svg
          viewBox="0 0 32 32"
          className="size-full [-webkit-user-drag:none]"
          aria-hidden
        >
          <circle cx="16" cy="6" r="4.5" className="fill-current" />
          <circle cx="7" cy="22" r="4.5" className="fill-current" />
          <circle cx="25" cy="22" r="4.5" className="fill-current" />
          <circle cx="16" cy="16" r="3" className="fill-gray-600" />
        </svg>
      </div>
    </div>
  );
}
